import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { DividerModule } from 'primeng/divider';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressSpinner } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { firstValueFrom } from 'rxjs';
import { ToastService } from '@workspace-app/shared/services/toast.service';
import { CertificateSharingService } from '../certificate-sharing.service';
import { ShareableOrganisationSelectComponent } from '../shareable-organisation-select/shareable-organisation-select.component';
import {
  CERTIFICATE_SHARING_LABELS,
  CertificateSharingKind,
  OrganisationCertificatePreview,
  ShareableOrganisation,
  ShareCertificatesDialogData,
  SharePreviewContact,
} from '../certificate-sharing.models';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Share Certificates": pick one organisation (supplier or coater, per `kind`), review its current
 * certificates and recipients, send everything in one email (FEAT-007).
 */
@Component({
  selector: 'app-share-certificates-dialog',
  standalone: true,
  imports: [
    FormsModule,
    DatePipe,
    ButtonModule,
    ChipModule,
    DividerModule,
    InputTextModule,
    MultiSelectModule,
    ProgressSpinner,
    TableModule,
    ShareableOrganisationSelectComponent,
  ],
  templateUrl: './share-certificates-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./share-certificates-dialog.component.scss'],
})
export class ShareCertificatesDialogComponent {
  private dialogRef = inject(DynamicDialogRef);
  private dialogConfig = inject(DynamicDialogConfig<ShareCertificatesDialogData>);
  private toastService = inject(ToastService);
  private certificateSharingService = inject(CertificateSharingService);

  kind: CertificateSharingKind = this.dialogConfig.data?.kind ?? 'approved-systems';
  labels = CERTIFICATE_SHARING_LABELS[this.kind];

  // Organisation picker (the select component loads the scoped list)
  selectedOrganisationId = signal<string | null>(null);

  // Preview of the selected organisation
  preview = signal<OrganisationCertificatePreview | null>(null);
  loadingPreview = signal(false);

  // Recipients
  selectedContacts = signal<SharePreviewContact[]>([]);
  additionalEmails = signal<string[]>([]);
  newEmail = '';
  newEmailInvalid = signal(false);

  sending = signal(false);

  certificateCount = computed(() => this.preview()?.certificates.length ?? 0);
  recipientCount = computed(() => this.selectedContacts().length + this.additionalEmails().length);
  canSend = computed(
    () => !this.sending() && !this.loadingPreview() && this.certificateCount() > 0 && this.recipientCount() > 0
  );

  async onOrganisationChange(organisation: ShareableOrganisation | null): Promise<void> {
    const organisationId = organisation?.organisationId ?? null;
    this.selectedOrganisationId.set(organisationId);
    this.preview.set(null);
    this.selectedContacts.set([]);
    this.additionalEmails.set([]);
    this.newEmail = '';
    this.newEmailInvalid.set(false);
    if (!organisationId) return;

    this.loadingPreview.set(true);
    try {
      const preview = await firstValueFrom(
        this.certificateSharingService.getOrganisationCertificatePreview(this.kind, organisationId)
      );
      // Ignore a late response if the user already picked another organisation
      if (this.selectedOrganisationId() !== organisationId) return;
      this.preview.set(preview);
      this.selectedContacts.set(preview.contacts.filter((c) => c.preselected));
    } catch (error) {
      this.toastService.displayErrorMessage(this.errorMessage(error, 'Failed to load the certificates'));
    } finally {
      this.loadingPreview.set(false);
    }
  }

  addEmail(): void {
    const email = this.newEmail.trim();
    if (!email) return;
    if (!EMAIL_PATTERN.test(email)) {
      this.newEmailInvalid.set(true);
      return;
    }
    const known = [
      ...this.additionalEmails(),
      ...this.selectedContacts().map((c) => c.email),
    ].map((e) => e.toLowerCase());
    if (!known.includes(email.toLowerCase())) {
      this.additionalEmails.update((list) => [...list, email]);
    }
    this.newEmail = '';
    this.newEmailInvalid.set(false);
  }

  removeEmail(email: string): void {
    this.additionalEmails.update((list) => list.filter((e) => e !== email));
  }

  onNewEmailInput(): void {
    this.newEmailInvalid.set(false);
  }

  async send(): Promise<void> {
    const organisationId = this.selectedOrganisationId();
    if (!organisationId || !this.canSend()) return;

    this.sending.set(true);
    try {
      const result = await firstValueFrom(
        this.certificateSharingService.shareOrganisationCertificates(this.kind, organisationId, {
          contactIds: this.selectedContacts().map((c) => c.id),
          additionalEmails: this.additionalEmails(),
        })
      );
      const certificates = result.certificateCount === 1 ? 'certificate' : 'certificates';
      const recipients = result.recipientCount === 1 ? 'recipient' : 'recipients';
      this.toastService.displaySuccessMessage(
        `${result.certificateCount} ${certificates} sent to ${result.recipientCount} ${recipients}`
      );
      this.dialogRef.close({ sent: true });
    } catch (error) {
      this.toastService.displayErrorMessage(this.errorMessage(error, 'The certificates could not be sent'));
    } finally {
      this.sending.set(false);
    }
  }

  close(): void {
    this.dialogRef.close({ sent: false });
  }

  private errorMessage(error: unknown, fallback: string): string {
    const message = (error as { error?: { message?: string } })?.error?.message;
    return message || fallback;
  }
}
